import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  useListBackstageLocationsQuery,
  useListCrewLocationsQuery,
} from '../../../redux/api/locationApi';
import {
  useCreateCrewDeliveryMutation,
  useGetDeliveredQuantitiesQuery,
} from '../../../redux/api/crewDeliveryApi';
import { useApiErrorToast } from '../../../hooks/useApiError';
import DestinationPicker from '../../CrewDelivery/create/DestinationPicker';
import ReturnDestinationCard from './ReturnDestinationCard';
import ReturnSummaryPanel from './ReturnSummaryPanel';

/**
 * Create a RETOURE (return) crew delivery: bring articles back from one or more
 * crew/backstage locations into the warehouse. Mirrors the outbound create flow
 * but is location-driven — the addable articles and the per-line cap come from
 * what was actually delivered to each location (net of prior returns), not from
 * warehouse stock. Submits the same endpoint with delivery_type 'RET'.
 */
const CreateCrewReturn = () => {
  const authState = useSelector((state) => state.auth);
  const eventId = authState?.currentEvent?.id;

  // Destinations are scoped to the selected event server-side.
  const { data: crewLocations } = useListCrewLocationsQuery(eventId, {
    skip: !eventId,
  });
  const { data: backstageLocations } = useListBackstageLocationsQuery(eventId, {
    skip: !eventId,
  });
  const [createDelivery, { isLoading }] = useCreateCrewDeliveryMutation();
  const navigate = useNavigate();
  const apiErrorToast = useApiErrorToast();

  // Each line holds the articles being returned from that destination:
  // { destination, destinationName, destinationType,
  //   articles: [{ articleId, articleName, quantity }] }
  const [returnLines, setReturnLines] = useState([]);
  const [comment, setComment] = useState('');
  const [noteComment, setNoteComment] = useState('');

  const destinationIds = returnLines.map((l) => l.destination).filter(Boolean);

  const { data: deliveredData, isFetching: deliveredFetching } =
    useGetDeliveredQuantitiesQuery(
      { eventId, destinationIds },
      { skip: !eventId || destinationIds.length === 0 }
    );

  // Returnable rows grouped per destination (drives each card's article picker)
  // and a flat cap lookup keyed `${destination_id}|${article_id}`.
  const deliveredByDestination = useMemo(() => {
    const map = {};
    (deliveredData?.results || []).forEach((row) => {
      (map[row.destination_id] = map[row.destination_id] || []).push(row);
    });
    return map;
  }, [deliveredData]);

  const deliveredLookup = useMemo(() => {
    const map = {};
    (deliveredData?.results || []).forEach((row) => {
      map[`${row.destination_id}|${row.article_id}`] = row;
    });
    return map;
  }, [deliveredData]);

  // Reset when the event changes to avoid stale data.
  useEffect(() => {
    setReturnLines([]);
    setComment('');
    setNoteComment('');
  }, [eventId]);

  const addLocation = (option) => {
    setReturnLines((prev) =>
      prev.some((l) => l.destination === option.id)
        ? prev
        : [
            ...prev,
            {
              destination: option.id,
              destinationName: option.name,
              destinationType: option.type,
              articles: [],
            },
          ]
    );
  };

  const removeLine = (destinationId) =>
    setReturnLines((prev) =>
      prev.filter((l) => l.destination !== destinationId)
    );

  const addArticleToLine = (destinationId, row) => {
    setReturnLines((prev) =>
      prev.map((line) =>
        line.destination === destinationId &&
        !line.articles.some((a) => a.articleId === row.article_id)
          ? {
              ...line,
              articles: [
                ...line.articles,
                {
                  articleId: row.article_id,
                  articleName: row.article_name,
                  quantity: 1,
                },
              ],
            }
          : line
      )
    );
  };

  const removeArticleFromLine = (destinationId, articleId) =>
    setReturnLines((prev) =>
      prev.map((line) =>
        line.destination === destinationId
          ? {
              ...line,
              articles: line.articles.filter((a) => a.articleId !== articleId),
            }
          : line
      )
    );

  const updateQuantity = (destinationId, articleId, quantity) =>
    setReturnLines((prev) =>
      prev.map((line) =>
        line.destination === destinationId
          ? {
              ...line,
              articles: line.articles.map((a) =>
                a.articleId === articleId ? { ...a, quantity } : a
              ),
            }
          : line
      )
    );

  // Per-article return totals across all destinations, for the summary panel.
  const summaryDetails = useMemo(() => {
    const map = {};
    returnLines.forEach((line) => {
      line.articles.forEach((a) => {
        const entry = (map[a.articleId] = map[a.articleId] || {
          articleId: a.articleId,
          articleName: a.articleName,
          total: 0,
        });
        entry.total += a.quantity || 0;
      });
    });
    return Object.values(map).filter((d) => d.total > 0);
  }, [returnLines]);

  // Block submit if any line returns more than was delivered there.
  const overReturned = useMemo(
    () =>
      returnLines.some((line) =>
        line.articles.some((a) => {
          const row = deliveredLookup[`${line.destination}|${a.articleId}`];
          return a.quantity > (row ? row.delivered : 0);
        })
      ),
    [returnLines, deliveredLookup]
  );

  const buildSubmitObj = () => {
    const items = returnLines.flatMap((line) =>
      line.articles
        .filter((a) => a.quantity > 0)
        .map((a) => ({
          article_id: a.articleId,
          planned_quantity: a.quantity,
          destination_id: line.destination,
          order_key: 1,
        }))
    );

    if (items.length === 0) return null;

    return {
      event_id: eventId,
      delivery_type: 'RET',
      items,
      ...(comment.trim() ? { comment: comment.trim() } : {}),
      ...(noteComment.trim()
        ? { delivery_note_comment: noteComment.trim() }
        : {}),
    };
  };

  const handleSubmit = async () => {
    const submitObj = buildSubmitObj();
    if (!submitObj) {
      toast.error('Retoure ist leer, alle Mengen sind 0. Bitte prüfen!');
      return;
    }

    try {
      const result = await createDelivery(submitObj).unwrap();
      toast.success('Retoure wurde angelegt');
      navigate(`/crew-delivery/${result.id}`);
    } catch (err) {
      apiErrorToast(err, 'Fehler beim Anlegen der Retoure');
    }
  };

  return (
    <div>
      <h4>Retoure anlegen</h4>

      <div className='row mt-3'>
        <div className='col-12 col-lg-8'>
          <DestinationPicker
            crewLocations={crewLocations}
            backstageLocations={backstageLocations}
            addedIds={destinationIds}
            onAdd={addLocation}
          />

          <div className='mt-4'>
            {returnLines.length === 0 ? (
              <p className='text-muted'>
                Noch keine Lieferstelle. Oben eine Crew- oder
                Backstage-Lieferstelle hinzufügen, von der zurückgegeben wird.
              </p>
            ) : (
              returnLines.map((line) => (
                <ReturnDestinationCard
                  key={line.destination}
                  line={line}
                  delivered={deliveredByDestination[line.destination] || []}
                  deliveredLookup={deliveredLookup}
                  isLoading={deliveredFetching}
                  onAddArticle={addArticleToLine}
                  onRemoveArticle={removeArticleFromLine}
                  onChangeQuantity={updateQuantity}
                  onRemove={removeLine}
                />
              ))
            )}
          </div>
        </div>

        <div className='col-12 col-lg-4 mt-4 mt-lg-0'>
          <ReturnSummaryPanel
            summaryDetails={summaryDetails}
            overReturned={overReturned}
            isLoading={isLoading}
            onSubmit={handleSubmit}
            comment={comment}
            onCommentChange={setComment}
            noteComment={noteComment}
            onNoteCommentChange={setNoteComment}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateCrewReturn;
